import { syncDiagnosticsEnabled, recordSyncTraffic, type SyncTrafficEntry } from './syncDiagnostics.js';
import type {
  NodeInfo,
  ManifestResponse,
  DocumentRead,
  SnapshotWrite,
  FeedEvent,
  UsageResponse,
  StorageErrorResponse,
} from './types.js';
import type { NodeTransport } from './transport.js';

export type TokenProvider = () => Promise<string | null> | string | null;

export interface HttpNodeTransportOptions {
  baseUrl: string;
  token?: string | TokenProvider;
  refreshToken?: TokenProvider;
  fetch?: typeof fetch;
}

export class StorageNodeError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown
  ) {
    super(message || code);
    this.name = 'StorageNodeError';
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export class HttpNodeTransport implements NodeTransport {
  private readonly baseUrl: string;
  private readonly token?: string | TokenProvider;
  private readonly refreshToken?: TokenProvider;
  private readonly fetchImpl: typeof fetch;

  constructor(options: HttpNodeTransportOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.token = options.token;
    this.refreshToken = options.refreshToken;
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  private async getAuthHeader(): Promise<Record<string, string>> {
    let tokenStr: string | null | undefined;
    if (typeof this.token === 'function') {
      tokenStr = await this.token();
    } else if (typeof this.token === 'string') {
      tokenStr = this.token;
    }
    if (tokenStr) {
      return { Authorization: `Bearer ${tokenStr}` };
    }
    return {};
  }

  private async request<T>(
    path: string,
    init: RequestInit = {}
  ): Promise<T> {
    const authHeaders = await this.getAuthHeader();
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      ...authHeaders,
      ...(init.headers as Record<string, string> | undefined),
    };

    if (init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const diagnostics = syncDiagnosticsEnabled();
    const operation = path.includes('/docs:batch') ? 'batch-read'
      : path.includes('/manifest') ? 'manifest'
      : path.includes('/snapshot') ? 'snapshot-write'
      : path.includes('/updates/') ? 'update-write'
      : path.includes('/docs/') ? (init.method === 'DELETE' ? 'delete' : 'document-read')
      : 'other';
    const segments = path.split('?')[0].split('/');
    const workspace = segments[3] ? decodeURIComponent(segments[3]) : '';
    const document = operation === 'document-read' || operation === 'snapshot-write' || operation === 'update-write'
      ? decodeURIComponent(segments[5] ?? '') : undefined;
    const requestBytes = typeof init.body === 'string' ? new TextEncoder().encode(init.body).byteLength : 0;
    const record = (response: Response, responseBytes: number, body?: unknown) => {
      if (!diagnostics) return;
      const entry: SyncTrafficEntry = {
        at: new Date().toISOString(), method: init.method ?? 'GET', operation, workspace, document,
        status: response.status, requestBytes, responseBytes,
        snapshots: 0, snapshotBytes: 0, updates: 0, updateBytes: 0, resets: 0,
      };
      const inspect = (value: unknown) => {
        if (!value || typeof value !== 'object') return;
        const doc = value as { snapshot?: { blob?: string }; updates?: { blob?: string }[]; reset?: boolean; id?: string };
        if (doc.snapshot?.blob) { entry.snapshots++; entry.snapshotBytes += new TextEncoder().encode(doc.snapshot.blob).byteLength; }
        if (Array.isArray(doc.updates)) for (const update of doc.updates) {
          entry.updates++; entry.updateBytes += new TextEncoder().encode(update.blob ?? '').byteLength;
        }
        if (doc.reset) entry.resets++;
        if (doc.id) {
          entry.documents ??= [];
          entry.documents.push({
            id: doc.id,
            jsonBytes: new TextEncoder().encode(JSON.stringify(doc)).byteLength,
            snapshots: doc.snapshot?.blob ? 1 : 0,
            snapshotBytes: doc.snapshot?.blob ? new TextEncoder().encode(doc.snapshot.blob).byteLength : 0,
            updates: Array.isArray(doc.updates) ? doc.updates.length : 0,
            updateBytes: Array.isArray(doc.updates) ? doc.updates.reduce((sum, update) => sum + new TextEncoder().encode(update.blob ?? '').byteLength, 0) : 0,
            resets: doc.reset ? 1 : 0,
          });
        }
      };
      if (Array.isArray(body)) body.forEach(inspect);
      else inspect(body);
      recordSyncTraffic(entry);
    };

    let res = await this.fetchImpl(url, { ...init, headers });
    if ((res.status === 401 || res.status === 403) && this.refreshToken) {
      if (diagnostics) record(res, Number(res.headers.get('content-length')) || 0);
      try {
        const freshToken = await this.refreshToken();
        if (freshToken) res = await this.fetchImpl(url, {
          ...init, headers: { ...headers, Authorization: `Bearer ${freshToken}` },
        });
      } catch { /* Report the original storage response. */ }
    }

    if (!res.ok) {
      let errBody: StorageErrorResponse | null = null;
      let errorBytes = 0;
      try {
        const body = await res.text();
        errorBytes = new TextEncoder().encode(body).byteLength;
        errBody = JSON.parse(body) as StorageErrorResponse;
      } catch { /* Not JSON */ }
      record(res, errorBytes);
      const code = errBody?.error || `HTTP_${res.status}`;
      const message = errBody?.message || res.statusText || code;
      throw new StorageNodeError(code, message, res.status, errBody);
    }

    if (res.status === 204) {
      record(res, 0);
      return undefined as unknown as T;
    }

    if (!diagnostics) return (await res.json()) as T;
    const raw = await res.text();
    const parsed = JSON.parse(raw) as T;
    record(res, new TextEncoder().encode(raw).byteLength, parsed);
    return parsed;
  }

  async discover(): Promise<NodeInfo> {
    const url = `${this.baseUrl}/.well-known/yada-storage`;
    const res = await this.fetchImpl(url, { method: 'GET' });
    if (!res.ok) {
      throw new StorageNodeError(
        'discovery_failed',
        `Discovery failed with status ${res.status}`,
        res.status
      );
    }
    return (await res.json()) as NodeInfo;
  }

  async manifest(ws: string, after?: string, since?: string): Promise<ManifestResponse> {
    const params = new URLSearchParams();
    if (after) params.set('after', after);
    if (since) params.set('since', since);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<ManifestResponse>(`/v1/ws/${encodeURIComponent(ws)}/manifest${qs}`, {
      method: 'GET',
    });
  }

  async head(ws: string, doc: string): Promise<boolean> {
    try {
      const authHeaders = await this.getAuthHeader();
      const url = `${this.baseUrl}/v1/ws/${encodeURIComponent(ws)}/docs/${encodeURIComponent(doc)}`;
      const res = await this.fetchImpl(url, {
        method: 'HEAD',
        headers: authHeaders,
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async read(ws: string, doc: string, since?: string): Promise<DocumentRead> {
    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    return this.request<DocumentRead>(
      `/v1/ws/${encodeURIComponent(ws)}/docs/${encodeURIComponent(doc)}${query}`,
      { method: 'GET' }
    );
  }

  async readBatch(ws: string, docs: (string | import('./types.js').BatchDocumentQuery)[]): Promise<DocumentRead[]> {
    const hasObjects = docs.some((d) => typeof d === 'object' && d !== null);
    const body = hasObjects
      ? {
          docs: docs.map((d) => (typeof d === 'string' ? { id: d } : d)),
        }
      : {
          ids: docs.map((d) => (typeof d === 'string' ? d : d.id)),
        };

    return this.request<DocumentRead[]>(
      `/v1/ws/${encodeURIComponent(ws)}/docs:batch`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  }

  async putUpdate(
    ws: string,
    doc: string,
    writer: string,
    counter: number,
    blob: Uint8Array | string
  ): Promise<{ cursor: string }> {
    const blobStr = typeof blob === 'string' ? blob : bytesToBase64(blob);
    return this.request<{ cursor: string }>(
      `/v1/ws/${encodeURIComponent(ws)}/docs/${encodeURIComponent(
        doc
      )}/updates/${encodeURIComponent(writer)}/${counter}`,
      {
        method: 'PUT',
        body: JSON.stringify({ blob: blobStr }),
      }
    );
  }

  async putSnapshot(ws: string, doc: string, snapshot: SnapshotWrite): Promise<{ cursor: string }> {
    return this.request<{ cursor: string }>(
      `/v1/ws/${encodeURIComponent(ws)}/docs/${encodeURIComponent(doc)}/snapshot`,
      {
        method: 'PUT',
        body: JSON.stringify(snapshot),
      }
    );
  }

  async tombstone(ws: string, doc: string): Promise<{ cursor: string }> {
    return this.request<{ cursor: string }>(
      `/v1/ws/${encodeURIComponent(ws)}/docs/${encodeURIComponent(doc)}`,
      {
        method: 'DELETE',
      }
    );
  }

  async putAsset(ws: string, id: string, data: Uint8Array, mimeType?: string): Promise<void> {
    const url = `${this.baseUrl}/v1/ws/${encodeURIComponent(ws)}/assets/${encodeURIComponent(id)}`;
    const authHeaders = await this.getAuthHeader();
    const headers: Record<string, string> = {
      ...authHeaders,
      'Content-Type': mimeType || 'application/octet-stream',
    };
    let res = await this.fetchImpl(url, {
      method: 'PUT',
      headers,
      body: data as any,
    });
    if ((res.status === 401 || res.status === 403) && this.refreshToken) {
      try {
        const freshToken = await this.refreshToken();
        if (freshToken) {
          res = await this.fetchImpl(url, {
            method: 'PUT',
            headers: {
              ...headers,
              Authorization: `Bearer ${freshToken}`,
            },
            body: data as any,
          });
        }
      } catch { /* Report the original response */ }
    }
    if (!res.ok) {
      throw new StorageNodeError('asset_upload_failed', `Asset upload failed: ${res.status} ${res.statusText}`, res.status);
    }
  }

  async getAsset(ws: string, id: string): Promise<{ data: Uint8Array; mimeType: string }> {
    const url = `${this.baseUrl}/v1/ws/${encodeURIComponent(ws)}/assets/${encodeURIComponent(id)}`;
    const authHeaders = await this.getAuthHeader();
    let res = await this.fetchImpl(url, {
      method: 'GET',
      headers: authHeaders,
    });
    if ((res.status === 401 || res.status === 403) && this.refreshToken) {
      try {
        const freshToken = await this.refreshToken();
        if (freshToken) {
          res = await this.fetchImpl(url, {
            method: 'GET',
            headers: {
              ...authHeaders,
              Authorization: `Bearer ${freshToken}`,
            },
          });
        }
      } catch { /* Report original response */ }
    }
    if (!res.ok) {
      throw new StorageNodeError('asset_fetch_failed', `Asset fetch failed: ${res.status} ${res.statusText}`, res.status);
    }
    const mimeType = res.headers.get('content-type') || 'application/octet-stream';
    const buffer = await res.arrayBuffer();
    return { data: new Uint8Array(buffer), mimeType };
  }

  async usage(ws: string): Promise<UsageResponse> {
    return this.request<UsageResponse>(`/v1/ws/${encodeURIComponent(ws)}/usage`, {
      method: 'GET',
    });
  }

  async *feed(
    ws: string,
    since?: string,
    signal?: AbortSignal
  ): AsyncIterable<FeedEvent> {
    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    const authHeaders = await this.getAuthHeader();
    const url = `${this.baseUrl}/v1/ws/${encodeURIComponent(ws)}/feed${query}`;

    const res = await this.fetchImpl(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...authHeaders,
      },
      signal,
    });

    if (!res.ok || !res.body) {
      throw new StorageNodeError(
        'feed_failed',
        `Feed failed with status ${res.status}`,
        res.status
      );
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        if (signal?.aborted) break;
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) {
            if (currentEvent === 'change' && currentData) {
              try {
                const parsed = JSON.parse(currentData) as FeedEvent;
                yield parsed;
              } catch {
                // Ignore parse errors on corrupted SSE line
              }
            }
            currentEvent = '';
            currentData = '';
            continue;
          }

          if (trimmed.startsWith('event:')) {
            currentEvent = trimmed.slice(6).trim();
          } else if (trimmed.startsWith('data:')) {
            currentData += trimmed.slice(5).trim();
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}
